/**
 * Helpers para ajustes de conceptos PLAME / Interna / Ambas.
 */

export function parseAjusteTemporal(valor) {
  if (valor == null) return null;
  if (typeof valor === 'object') {
    return {
      monto: valor.monto,
      destino: valor.destino || 'plame',
    };
  }
  return { monto: valor, destino: null };
}

export function getMontoDesdeAjuste(valor, fallback) {
  const parsed = parseAjusteTemporal(valor);
  if (!parsed || parsed.monto === undefined || parsed.monto === null || parsed.monto === '') {
    return fallback;
  }
  return parsed.monto;
}

export function getDestinoDesdeAjuste(valor, defaultDestino = 'plame') {
  const parsed = parseAjusteTemporal(valor);
  return parsed?.destino || defaultDestino;
}

export function destinoAplicaPlame(destino) {
  const d = String(destino || 'plame').toLowerCase();
  return d === 'plame' || d === 'ambas';
}

export function destinoAplicaInterna(destino) {
  const d = String(destino || 'plame').toLowerCase();
  return d === 'interna' || d === 'ambas';
}

/**
 * Un concepto con destino solo Interna no debe listarse en boleta PLAME.
 * Excepción: 0121 (sueldo básico dual) conserva el monto PLAME original.
 */
export function conceptoVaABoletaPlame(concepto) {
  const dest = String(concepto?.destino_ajuste || concepto?.destino || 'plame').toLowerCase();
  if (dest !== 'interna') return true;
  return codigoConceptoAjuste(concepto) === '0121';
}

export function montoInternaDesdeAjuste(concepto) {
  const dest = String(concepto?.destino_ajuste || 'plame').toLowerCase();
  if (dest === 'interna') {
    if (concepto?.monto_interna == null || concepto.monto_interna === '') return null;
    const n = Number(concepto.monto_interna);
    return Number.isFinite(n) ? n : null;
  }
  if (dest === 'ambas') {
    if (concepto?.monto_interna != null && concepto.monto_interna !== '') {
      const n = Number(concepto.monto_interna);
      return Number.isFinite(n) ? n : Number(concepto?.monto) || 0;
    }
    return Number(concepto?.monto) || 0;
  }
  return null;
}

/**
 * Pasa consideraciones destino Interna/Ambas a ingresosInternos / descuentosInternos.
 * El 0121 interna actualiza sueldo interno; no se duplica como extra.
 */
export function fusionarAjustesEnInterna({
  ingresos = [],
  descuentos = [],
  ingresosInternos = [],
  descuentosInternos = [],
} = {}) {
  const cloneIng = (ingresosInternos || []).map((c) => ({ ...c }));
  const cloneDes = (descuentosInternos || []).map((c) => ({ ...c }));
  let sueldoInternoOverride = null;

  const upsert = (concepto, bucket, esIngreso) => {
    if (!destinoAplicaInterna(concepto?.destino_ajuste)) return;
    const monto = montoInternaDesdeAjuste(concepto);
    if (monto == null || !Number.isFinite(monto) || monto < 0) return;
    const codigo = codigoConceptoAjuste(concepto);
    if (!codigo) return;
    if (esIngreso && codigo === '0121') {
      sueldoInternoOverride = monto;
      return;
    }
    if (monto <= 0) return;
    const existente = bucket.find((x) => codigoConceptoAjuste(x) === codigo);
    if (existente) {
      existente.monto = monto;
      existente.origenCalculo = existente.origenCalculo || 'ajuste_sueldo_interna';
      return;
    }
    bucket.push({
      id: concepto.plame_configuracion_id || concepto.id,
      codigo,
      codigo_plame: codigo,
      codigoPlame: codigo,
      monto,
      tipo_concepto: esIngreso ? 'ingreso' : 'descuento',
      descripcion: concepto.descripcion || concepto.concepto || codigo,
      origenCalculo: 'ajuste_sueldo_interna',
      destino_ajuste: String(concepto.destino_ajuste).toLowerCase(),
    });
  };

  (ingresos || []).forEach((c) => upsert(c, cloneIng, true));
  (descuentos || []).forEach((c) => upsert(c, cloneDes, false));

  return {
    ingresosInternos: cloneIng,
    descuentosInternos: cloneDes,
    sueldoInternoOverride,
  };
}

export function codigoConceptoAjuste(concepto) {
  return String(
    concepto?.codigo
      || concepto?.codigoPlame
      || concepto?.codigo_plame
      || concepto?.plame_configuracion?.codigo_plame
      || ''
  );
}

/**
 * Resuelve montos a persistir según destino.
 * - plame: monto PLAME = nuevo; monto_interna sin cambio (null si no había)
 * - interna: monto PLAME = original; monto_interna = nuevo
 * - ambas: ambos = nuevo
 */
export function resolverMontosPorDestino({
  montoOriginal,
  montoNuevo,
  destino,
  montoInternaPrevio = null,
}) {
  const dest = String(destino || 'plame').toLowerCase();
  const original = Number(montoOriginal) || 0;
  const nuevo = montoNuevo !== undefined && montoNuevo !== null && montoNuevo !== ''
    ? Number(montoNuevo)
    : original;

  if (dest === 'interna') {
    return {
      monto: original,
      monto_interna: nuevo,
      destino_ajuste: 'interna',
    };
  }
  if (dest === 'ambas') {
    return {
      monto: nuevo,
      monto_interna: nuevo,
      destino_ajuste: 'ambas',
    };
  }
  return {
    monto: nuevo,
    monto_interna: montoInternaPrevio != null ? Number(montoInternaPrevio) : null,
    destino_ajuste: 'plame',
  };
}

export default {
  parseAjusteTemporal,
  getMontoDesdeAjuste,
  getDestinoDesdeAjuste,
  destinoAplicaPlame,
  destinoAplicaInterna,
  conceptoVaABoletaPlame,
  montoInternaDesdeAjuste,
  fusionarAjustesEnInterna,
  codigoConceptoAjuste,
  resolverMontosPorDestino,
};
